import { memo, type ReactNode, useMemo } from 'react';

import { type SearchBoardsResponse, type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/api/types';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { SearchCommandBoardList } from '~/components/SearchCommand/SearchCommandBoardList';
import { SearchCommandListFrame } from '~/components/SearchCommand/SearchCommandListFrame';
import { SearchCommandSavedFilterList } from '~/components/SearchCommand/SearchCommandSavedFilterList';
import { SearchCommandSimilarImageSearch } from '~/components/SearchCommand/SearchCommandSimilarImageSearch';
import { SearchCommandSuggestionList } from '~/components/SearchCommand/SearchCommandSuggestionList';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type SearchCommandListProps = {
  hideSuggestions: boolean;
  isOpen: boolean;
  listStart: ReactNode | undefined;
  onBoardClick: (board: SearchBoardsResponse) => void;
  onSavedFilterSelect: (savedFilter: SearchSavedFiltersResponse) => void;
  onSimilarImageSearch: (base64Image: string) => void;
  onSuggestionClick: (suggestion: string) => void;
  renderCustomContent?: () => ReactNode;
  searchValue?: string;
  suggestions?: SuggestionsResponse;
};

export const SearchCommandList = memo(
  ({
    hideSuggestions,
    isOpen,
    listStart,
    onBoardClick,
    onSavedFilterSelect,
    onSimilarImageSearch,
    onSuggestionClick,
    renderCustomContent,
    searchValue,
    suggestions,
  }: SearchCommandListProps) => {
    const { similarToImageSearchAvailable } = usePrivateSearchUIHelpers();
    const { ui2026 } = useTemporaryFeatureFlags();
    const showSimilarImageSearch = similarToImageSearchAvailable && !ui2026 && isOpen && !hideSuggestions;

    const showSearchCommandList = useMemo(() => {
      const hasSearchTerm = searchValue && searchValue.trim().length > 0;
      const hasSuggestions = !!suggestions?.suggestions?.length;
      const hasSavedFilters = !!suggestions?.savedFilters?.length;
      const hasBoards = !!suggestions?.boards?.length;
      const hasSearchResults = hasSuggestions || hasSavedFilters || hasBoards;
      const hasCustomContent = !!renderCustomContent;

      const shouldShowForSearch = isOpen && !hideSuggestions && (hasSearchTerm || hasSearchResults);

      const shouldShowForCustomContent = isOpen && hasCustomContent;

      return shouldShowForSearch || shouldShowForCustomContent || showSimilarImageSearch;
    }, [isOpen, hideSuggestions, searchValue, suggestions, renderCustomContent, showSimilarImageSearch]);

    if (!showSearchCommandList) {
      return null;
    }

    return (
      <SearchCommandListFrame>
        {renderCustomContent ? (
          renderCustomContent()
        ) : (
          <>
            {ui2026 ? (
              <>
                <SearchCommandSuggestionList onSelect={onSuggestionClick} suggestions={suggestions?.suggestions} />
                {listStart}
              </>
            ) : (
              <>
                {listStart}
                <SearchCommandSuggestionList onSelect={onSuggestionClick} suggestions={suggestions?.suggestions} />
              </>
            )}

            {!!suggestions?.savedFilters.length && !!onSavedFilterSelect && (
              <SearchCommandSavedFilterList onSelect={onSavedFilterSelect} savedFilters={suggestions.savedFilters} />
            )}

            {!!suggestions?.boards.length && (
              <SearchCommandBoardList boards={suggestions.boards} onBoardClick={onBoardClick} />
            )}

            {showSimilarImageSearch && <SearchCommandSimilarImageSearch onSearch={onSimilarImageSearch} />}
          </>
        )}
      </SearchCommandListFrame>
    );
  },
);

SearchCommandList.displayName = 'SearchCommandList';
